import { CBCore, CBSocketClient, IS_SOCKET_ERROR, SocketClient } from "cbcore-ts"
import { IS, nil, NO, UIRoute, UIViewBroadcastEvent, YES } from "uicore-ts"
import { RHColor } from "../Custom components/RH/RHColor"
import {
    RHCompanyAffiliation,
    RHCompanyUnseenCounts,
    RHNotificationAcknowledgementState,
    RHUnseenCounts,
    RHUserProfile
} from "../SocketClientFunctions"
import { RHCompanyAffiliationStore } from "./RHCompanyAffiliationStore"
import { RHContractStore } from "./RHContractStore"
import { RHNotificationStore } from "./RHNotificationStore"
import { RHOffererConfigurationStore } from "./RHOffererConfigurationStore"
import { RHOfferStore } from "./RHOfferStore"
import { RHPrivateMessageStore } from "./RHPrivateMessageStore"
import {
    RHProcurementAmendedPayload,
    RHProcurementDeadlineChangedPayload,
    RHProcurementStore
} from "./RHProcurementStore"
import { RHQAStore } from "./RHQAStore"


export type RHApplicationMode = "procurer" | "offerer" | "auditor"


/**
 * RHCore — project-level application model.
 *
 * Extends CBCore with session state that is specific to this project.
 * The singleton is registered at app startup via `CBCore.registerSharedInstance`
 * so that all library-internal calls to `CBCore.sharedInstance` also return
 * this instance.
 *
 * All application code should reference `RHCore.sharedInstance` rather than
 * `CBCore.sharedInstance` to get the correctly typed instance.
 *
 * ## Startup
 *
 * In the app entry point, before anything else:
 *
 * ```typescript
 * CBCore.registerSharedInstance(new RHCore())
 * CBCore.initIfNeededWithViewCore(new UICore("root", RootViewController))
 * ```
 */
export class RHCore extends CBCore {

    static override broadcastEventName = {
        ...CBCore.broadcastEventName,
        /**
         * Fired whenever the current user's company affiliation changes — either
         * from a server push or after an explicit refresh. The cache in
         * `currentCompanyAffiliation` is always updated before this fires.
         */
        currentCompanyAffiliationDidChange: "CurrentCompanyAffiliationDidChange",
        /**
         * Fired when the server restarts with a new webclient bundle, indicating
         * the client should reload the page at its earliest convenience.
         */
        newBuildAvailable: "NewBuildAvailable",
        /**
         * Fired when the user switches between procurer, offerer, and auditor
         * application modes. All mode-sensitive UI should respond to this event.
         */
        applicationModeDidChange: "ApplicationModeDidChange",
        /**
         * Fired whenever the current user's available company affiliations are
         * refreshed.
         */
        companyAffiliationsDidChange: "CompanyAffiliationsDidChange",
        /**
         * Fired whenever the user changes the selected acting company.
         */
        activeCompanyDidChange: "ActiveCompanyDidChange",
        /**
         * Fired when the server pushes a `ProcurementDeadlineChanged` message
         * to this client, meaning a procurement the user is registered for has
         * had its deadline changed. The event carries the push payload so
         * listeners can display a notification dialog.
         * Payload shape: `RHProcurementDeadlineChangedPayload`
         */
        procurementDeadlineChanged: "ProcurementDeadlineChanged",
        /**
         * Fired when the server pushes a published-procurement amendment.
         * This supersedes the deadline-only event while the legacy push remains
         * supported during backend rollout.
         */
        procurementAmended: "ProcurementAmended",
        /**
         * Fired whenever the unseen badge counts are updated — either from the
         * welcome message on login or after a mark-seen call resolves.
         * All badge-displaying UI should respond to this event.
         */
        unseenEventCountDidChange: "UnseenEventCountDidChange",
        /**
         * Fired whenever the number of pending company join requests changes.
         * `CompanyViewController` and `TopBarView` should respond to this event
         * to update their respective badges.
         */
        pendingJoinRequestCountDidChange: "PendingJoinRequestCountDidChange",
        /**
         * Fired whenever the current user's procurement favorites ("lemmikud")
         * change (optimistically on toggle, and again if the server write fails and
         * the change is reverted). Every list view showing the favorite star / the
         * "Lemmikud" filter should respond by repainting.
         */
        favoritesDidChange: "FavoritesDidChange",
        /** Fired when the top bar's default company-scope (single vs. all companies) changes. */
        defaultAllCompaniesScopeDidChange: "DefaultAllCompaniesScopeDidChange",
        /**
         * Fired whenever a field on the logged-in user's own profile changes
         * in place (e.g. isPasswordChangeRequired clearing after a password
         * change) without a full re-login, so UI reflecting profile state -
         * chrome included - can refresh without waiting for userDidLogIn.
         */
        userProfileDidChange: "UserProfileDidChange"
    } as const


    private static readonly _applicationModeStorageKey = "RHCore_applicationMode"


    /**
     * Central store for all procurement reads and writes.
     * Views must never call SocketClient directly for procurement operations —
     * they must go through this store instead.
     */
    readonly procurementStore = new RHProcurementStore(this)

    /**
     * Central store for offer list reads on both the procurer and offerer sides.
     * Views must never call SocketClient directly for offer list operations —
     * they must go through this store instead.
     */
    readonly offerStore = new RHOfferStore(this)

    /**
     * Central store for contract reads, writes, and realtime invalidation.
     */
    readonly contractStore = new RHContractStore(this)

    /**
     * General-purpose store for per-entity unseen notification counts.
     * Domain stores populate counts via `setCount` / `setCounts` when they
     * receive server data. Views call `markSeen` to clear badges and subscribe
     * to receive notifications when counts change.
     */
    readonly notificationStore = new RHNotificationStore(this)

    /**
     * Central store for active acting-company affiliation. New company-scoped
     * frontend code reads the company id from here instead of relying on
     * implicit "current company" server semantics.
     */
    readonly companyAffiliationStore = new RHCompanyAffiliationStore(this)

    /**
     * Central store for procurement Q&A questions.
     * Views must never call SocketClient directly for question operations —
     * they must go through this store instead. The store caches questions
     * per procurement and invalidates the cache when the server pushes
     * ProcurementQuestionsDidChange.
     */
    readonly qaStore = new RHQAStore(this)

    /**
     * Central store for procurement private messaging (procurer ↔ one
     * offerer company, per procurement). Views must never call SocketClient
     * directly for private-message operations — they must go through this
     * store instead. Threads are cached per procurement and invalidated
     * when the server pushes ProcurementPrivateMessagesDidChange.
     */
    readonly privateMessageStore = new RHPrivateMessageStore(this)

    /**
     * Offerer-side company defaults and personal configuration overrides.
     */
    readonly offererConfigurationStore = new RHOffererConfigurationStore(this)


    /**
     * The single client-side source of truth for the current user's procurement
     * favorites ("lemmikud"). Seeded from the welcome message in userProfileDidChange;
     * read by list views to draw the star + apply the "Lemmikud" filter; mutated
     * only through toggleProcurementFavorite (optimistic + broadcast + persist).
     */
    private _favoriteProcurementIds = new Set<string>()


    /**
     * The current user's roles and permissions within their company.
     * Private — consumers read `currentCompanyAffiliation`, which handles
     * in-flight deduplication and returns `undefined` on failure.
     */
    _currentCompanyAffiliation?: RHCompanyAffiliation

    /**
     * Holds the in-flight promise so that concurrent callers awaiting the same
     * fetch coalesce onto one network round-trip.
     */
    _currentCompanyAffiliationPromise?: Promise<
        RHCompanyAffiliation | undefined
    >

    /**
     * The build token received from the server on the first socket connection.
     * Subsequent connections compare against this value to detect a server restart.
     */
    private _knownBuildToken?: string

    /**
     * Resolver for the current `userDataReady` promise. Called once all user
     * data has been fetched at the end of `userProfileDidChange`.
     */
    private _userDataReadyResolve?: (value: boolean) => void

    /**
     * Resolves with `true` when the user is logged in and all data has been
     * fetched, or `false` after a logout cycle completes.
     * Consumers (e.g. `UserMainViewController`) should await this before
     * rendering user-specific UI to avoid a blank-frame flicker.
     *
     * Initialised to an already-resolved promise so that any call made before
     * the very first `userProfileDidChange` does not hang.
     */
    userDataReady: Promise<boolean> = Promise.resolve(NO)

    /**
     * Resolves once `userProfileDidChange` has completed its first cycle —
     * regardless of whether the user is logged in or not. Consumers that need
     * the definitive session state before acting (e.g. `_applyCompanyStatus`)
     * should await this instead of `userDataReady`, because `userDataReady` is
     * pre-resolved to `false` and would let consumers race ahead on a refresh
     * before the first cycle has run.
     *
     * Stays pending until the first `userProfileDidChange` completes, then
     * resolves permanently — subsequent cycles do not reset it.
     */
    readonly sessionInitialised: Promise<void> = new Promise<void>(resolve => {
        this._resolveSessionInitialised = resolve
    })
    private _resolveSessionInitialised!: () => void

    constructor() {
        super()

        this.procurementStore._onDeadlineChanged = (payload: RHProcurementDeadlineChangedPayload) => {
            this.broadcastMessageInRootViewTree({
                name: RHCore.broadcastEventName.procurementDeadlineChanged,
                payload
            } as UIViewBroadcastEvent & { payload: RHProcurementDeadlineChangedPayload })
        }
        this.procurementStore._onProcurementAmended = (payload: RHProcurementAmendedPayload) => {
            this.broadcastMessageInRootViewTree({
                name: RHCore.broadcastEventName.procurementAmended,
                payload
            } as UIViewBroadcastEvent & { payload: RHProcurementAmendedPayload })
        }

        // CRITICAL: push listeners must be (re-)registered on EVERY socket client.
        // CBCore.reloadSocketConnection() — which runs on login and logout via the
        // server's CBPerformReconnect — replaces the whole CBSocketClient object,
        // silently dropping every listener registered on the previous instance.
        // Registering only once in the constructor therefore left the client deaf
        // to all server pushes after an in-page login (badges and lists only
        // updated after F5, which re-registers listeners on a fresh client).
        // _functionsToCallForEachSocketClient is the framework's official hook for
        // exactly this situation.
        this._registerSocketPushListeners()
        this._functionsToCallForEachSocketClient.push(() => this._registerSocketPushListeners())
    }


    /**
     * Registers all RHCore-level server-push listeners on the CURRENT socket
     * client. Called once at construction and again after every socket client
     * replacement (login/logout reconnect) via _functionsToCallForEachSocketClient.
     */
    private _registerSocketPushListeners() {
        this.socketClient.addTargetForMessagesForKey(
            "ClientShouldReload",
            (message: { buildToken: string }) => {
                if (!this._knownBuildToken) {
                    this._knownBuildToken = message.buildToken
                    return
                }
                if (this._knownBuildToken !== message.buildToken) {
                    console.log("RHCore: server restarted with a new build — broadcasting.")
                    this.broadcastMessageInRootViewTree({
                        name: RHCore.broadcastEventName.newBuildAvailable
                    } as UIViewBroadcastEvent)
                }
            }
        )

        this.socketClient.addTargetForMessagesForKey(
            "UnseenCountsDidChange",
            (counts: RHCompanyUnseenCounts) => {
                this.updateUnseenCounts(counts)
            }
        )

        this.socketClient.addTargetForMessagesForKey(
            "NotificationAcknowledgementDidChange",
            (state: RHNotificationAcknowledgementState) => {
                this.notificationStore.applyAcknowledgementState(state)
            }
        )

        this.socketClient.addTargetForMessagesForKey(
            "IncomingOffersDidChange",
            (payload: { changedProcurementIds: string[] }) => {
                this.offerStore._onIncomingOffersDidChange(payload.changedProcurementIds ?? [])
            }
        )

        this.socketClient.addTargetForMessagesForKey(
            "OutgoingOffersDidChange",
            (payload: { changedProcurementIds?: string[] }) => {
                this.offerStore._onOutgoingOffersDidChange(payload.changedProcurementIds ?? [])
            }
        )

        this.socketClient.addTargetForMessagesForKey(
            "OfferMessageReceived",
            (payload: { procurementOfferId: string }) => {
                this.offerStore._onOfferMessageReceived(payload.procurementOfferId)
                // Refresh the per-procurement/per-offer unread-message map so the
                // row and tab dots light up live, not only after a view re-entry.
                this.notificationStore.reloadOfferMessageCounts()
            }
        )

        this.socketClient.addTargetForMessagesForKey(
            "CurrentCompanyAffiliationDidChange",
            (payload) => {
                this._currentCompanyAffiliation = payload
                this._currentCompanyAffiliationPromise = undefined
                this.broadcastMessageInRootViewTree({
                    name: RHCore.broadcastEventName.currentCompanyAffiliationDidChange
                } as UIViewBroadcastEvent)
            }
        )

    }


    /**
     * Returns the shared singleton, typed as `RHCore`.
     *
     * `CBCore.registerSharedInstance(new RHCore())` must have been called at
     * startup for this to return an `RHCore` instance.
     */
    static override get sharedInstance(): RHCore {
        return CBCore.sharedInstance as RHCore
    }


    /**
     * The current application mode — "procurer", "offerer", or "auditor".
     * Persisted to localStorage so it survives page reloads.
     * Defaults to "procurer" if nothing has been stored yet.
     *
     * A mode is available only when the company affiliation contains its matching
     * role flag. The top bar derives its visible segments from the same flags.
     */
    get applicationMode(): RHApplicationMode {
        // Route is the primary source of truth for the current session.
        const routeValue = UIRoute.currentRoute
            .componentWithName("settings")?.parameters["mode"]
        if (routeValue === "auditor" && this._applicationModeIsAvailable("auditor")) {
            return "auditor"
        }
        if (routeValue === "offerer" && this._applicationModeIsAvailable("offerer")) {
            return "offerer"
        }
        if (routeValue === "procurer" && this._applicationModeIsAvailable("procurer")) {
            return "procurer"
        }

        return this.preferredApplicationMode
    }


    /** Last valid mode selected for the active company, ignoring a copied deep link's route mode. */
    get preferredApplicationMode(): RHApplicationMode {
        const stored = localStorage.getItem(this._applicationModeStorageKey())
        if (stored === "auditor" && this._applicationModeIsAvailable("auditor")) {
            return "auditor"
        }
        if (stored === "offerer" && this._applicationModeIsAvailable("offerer")) {
            return "offerer"
        }
        if (stored === "procurer" && this._applicationModeIsAvailable("procurer")) {
            return "procurer"
        }
        if (this._applicationModeIsAvailable("procurer")) {
            return "procurer"
        }
        if (this._applicationModeIsAvailable("offerer")) {
            return "offerer"
        }
        if (this._applicationModeIsAvailable("auditor")) {
            return "auditor"
        }
        return "procurer"
    }

    set applicationMode(mode: RHApplicationMode) {
        if (IS(this._currentCompanyAffiliation) && !this._applicationModeIsAvailable(mode)) {
            return
        }
        const storageKey = this._applicationModeStorageKey()
        const previous = localStorage.getItem(storageKey)
        if (previous === mode) {
            return
        }
        localStorage.setItem(storageKey, mode)
        RHColor.applySemanticColors()
        this.broadcastMessageInRootViewTree({
            name: RHCore.broadcastEventName.applicationModeDidChange
        } as UIViewBroadcastEvent)
    }


    /**
     * Convenience boolean — true when the user has chosen offerer mode.
     */
    get isInOffererMode(): boolean {
        return this.applicationMode === "offerer"
    }

    get isInAuditorMode(): boolean {
        return this.applicationMode === "auditor"
    }


    _applicationModeIsAvailable(mode: RHApplicationMode): boolean {
        const affiliation = this.companyAffiliationStore.activeCompanyAffiliation ?? this._currentCompanyAffiliation
        if (mode === "procurer") {
            return RHCore.userCanActAsProcurerThroughAffiliation(affiliation)
        }
        if (mode === "offerer") {
            return affiliation?.isOfferer == YES
        }
        return affiliation?.isAuditor == YES
    }


    /**
     * Shared by _applicationModeIsAvailable and TopBarView's mode-selector/
     * nav checks. A company without riigihangete register history as a
     * procuring entity (CompanyAccessService.companyIsEligibleToActAsProcurer)
     * can never act as a procurer, even when an isProcurer/canCreateProcurements/
     * canManageProcurements flag from before the eligibility gate says otherwise.
     */
    static userCanActAsProcurerThroughAffiliation(affiliation?: RHCompanyAffiliation): boolean {
        if (affiliation?.isEligibleToActAsProcurer != YES) {
            return NO
        }
        return affiliation?.isProcurer == YES ||
            affiliation?.canCreateProcurements == YES ||
            affiliation?.canManageProcurements == YES
    }


    static isCandidateIdAValidObjectId(candidateId: string): boolean {
        return /^[a-f\d]{24}$/i.test(candidateId)
    }


    _applicationModeStorageKey(): string {
        return this.companyAffiliationStore._applicationModeStorageKey()
    }


    /**
     * The current per-user unseen badge counts.
     * Delegates to notificationStore, which is the single source of truth
     * for all badge counts. Returns an all-zeros object before the first
     * login or if counts have not yet been received from the server.
     */
    get unseenCounts(): RHUnseenCounts {
        return this.notificationStore.globalCounts
    }


    /**
     * The current number of pending company join requests.
     * Sourced from notificationStore.globalCounts.pendingJoinRequests.
     * The setter allows the administration view to patch the value locally
     * after approving or rejecting a request, without waiting for a full
     * counts refresh.
     */
    get pendingJoinRequestCount(): number {
        return this.unseenCounts.pendingJoinRequests
    }

    set pendingJoinRequestCount(count: number) {
        const current = this.notificationStore.globalCounts
        if (current.pendingJoinRequests === count) {
            return
        }
        const companyId = this.companyAffiliationStore.activeCompanyId
        if (!companyId) {
            return
        }
        this.notificationStore.updateGlobalCounts({
            companyId,
            counts: {
                ...current,
                pendingJoinRequests: count
            }
        })
    }


    /**
     * Stores updated unseen counts and broadcasts UnseenEventCountDidChange
     * so badge-displaying views can refresh themselves.
     * Also broadcasts pendingJoinRequestCountDidChange when the pending
     * join request count has changed, so company UI updates immediately.
     *
     * Delegates storage to notificationStore, which is now the single
     * source of truth for all badge counts. Called after:
     * - Welcome message extraction in userProfileDidChange
     * - Any mark-seen response that carries updated counts
     * - UnseenCountsDidChange socket push
     */
    updateUnseenCounts(counts: RHCompanyUnseenCounts) {
        this.notificationStore.updateGlobalCounts(counts)
    }


    /**
     * Fires the view-tree broadcast events that badge-displaying views
     * depend on. Called by RHNotificationStore.updateGlobalCounts so the
     * broadcasts always fire whenever global counts change, regardless of
     * the update path.
     *
     * @param pendingJoinRequestCountChanged - true when pendingJoinRequests
     *   changed from its previous value, triggering the separate
     *   pendingJoinRequestCountDidChange broadcast.
     */
    broadcastGlobalCountsDidChange(pendingJoinRequestCountChanged: boolean) {
        this.broadcastMessageInRootViewTree({
            name: RHCore.broadcastEventName.unseenEventCountDidChange
        } as UIViewBroadcastEvent)
        if (pendingJoinRequestCountChanged) {
            this.broadcastMessageInRootViewTree({
                name: RHCore.broadcastEventName.pendingJoinRequestCountDidChange
            } as UIViewBroadcastEvent)
        }
    }


    /**
     * Returns the user-facing error message carried by a failed SocketClient
     * response, or `undefined` if the response is not an error.
     *
     * A server `sendErrorResponse(...)` resolves the awaited call with
     * `result === undefined` and the error under `errorResult` — it does not
     * reject. `IS_SOCKET_ERROR` is the framework's own error detector;
     * the message text lives on `errorResult.messageData`. Pass
     * `fallbackMessage` for the case where the server sent no readable string.
     *
     * This is the single home for this projection — views and stores must not
     * re-implement it; call it through the core.
     */
    errorMessageFromResponse(
        response: { errorResult?: unknown },
        fallbackMessage?: string
    ): string | undefined {
        if (!IS_SOCKET_ERROR(response.errorResult)) {
            return undefined
        }
        const messageData = response.errorResult.messageData
        if (typeof messageData === "string" && messageData.length > 0) {
            return messageData
        }
        return fallbackMessage
    }


    messageByAddingActiveCompanyId<T extends Record<string, unknown>>(
        message: T
    ): T & { companyId?: string } {
        const activeCompanyId = this.companyAffiliationStore.activeCompanyId
        if (activeCompanyId && activeCompanyId.length > 0) {
            return {
                ...message,
                companyId: activeCompanyId
            }
        }
        return message
    }


    /**
     * Returns the current user's company affiliation.
     *
     * - If the cache is already populated, returns it immediately.
     * - If a fetch is already in flight, awaits that same promise
     *   (no duplicate network request).
     * - Otherwise starts a new fetch, caches the result, and returns it.
     * - Returns `undefined` if the user is not logged in or the fetch fails.
     */
    get currentCompanyAffiliation(): Promise<RHCompanyAffiliation | undefined> | RHCompanyAffiliation | undefined {

        if (!this.isUserLoggedIn) {
            return undefined
        }

        const activeAffiliation = this.companyAffiliationStore.activeCompanyAffiliation
        if (IS(activeAffiliation)) {
            this._currentCompanyAffiliation = activeAffiliation
            return activeAffiliation
        }

        if (IS(this._currentCompanyAffiliation)) {
            return this._currentCompanyAffiliation
        }

        if (IS(this._currentCompanyAffiliationPromise)) {
            return this._currentCompanyAffiliationPromise
        }

        return this._loadCurrentCompanyAffiliation()

    }


    /**
     * Clears the cache and any in-flight promise, then immediately starts a
     * fresh fetch. Call this after any mutation that may have changed the
     * user's company membership or roles.
     */
    async refreshCurrentCompanyAffiliation() {
        this._currentCompanyAffiliation = undefined
        this._currentCompanyAffiliationPromise = undefined
        await this._loadCurrentCompanyAffiliation()
        this.broadcastMessageInRootViewTree({
            name: RHCore.broadcastEventName.currentCompanyAffiliationDidChange
        } as UIViewBroadcastEvent)
    }


    async _loadCurrentCompanyAffiliation() {

        const userProfile = this.userProfile
        if (!IS(userProfile)) {
            return undefined
        }

        const statusPromise = this.companyAffiliationStore.retrieveMyCompanyAffiliations(YES)
            .then(async () => {
                if (this.userProfile !== userProfile || this._currentCompanyAffiliationPromise !== statusPromise) {
                    return undefined
                }
                let result = this.companyAffiliationStore.activeCompanyAffiliation ??
                    this.companyAffiliationStore.directCompanyAffiliation
                if (!result) {
                    result = (await SocketClient.RetrieveDirectCompanyAffiliationForCurrentUser()).result
                }
                if (this.userProfile !== userProfile || this._currentCompanyAffiliationPromise !== statusPromise) {
                    return undefined
                }
                this._currentCompanyAffiliation = result
                return result
            })
            .catch(error => {
                if (this.userProfile !== userProfile || this._currentCompanyAffiliationPromise !== statusPromise) {
                    return undefined
                }
                console.error("RHCore: failed to load the current company affiliation", error)
                this._currentCompanyAffiliation = undefined
                return undefined
            })
            .finally(() => {
                if (this._currentCompanyAffiliationPromise === statusPromise) {
                    this._currentCompanyAffiliationPromise = undefined
                }
            })
        this._currentCompanyAffiliationPromise = statusPromise
        return statusPromise

    }


    /**
     * Overrides the base implementation to load the company affiliation before the
     * `userDidLogIn` broadcast fires.
     *
     * On login:  registers the push listener, reads and strips embedded
     *            `unseenCounts` from the user profile, loads the company affiliation,
     *            then calls super (which broadcasts `userDidLogIn`).
     *
     * On logout: clears cached status and counts, then calls super (which
     *            broadcasts `userDidLogOut`).
     *
     * In both cases `userDataReady` is replaced with a fresh promise at the
     * start and resolved at the end, so UI that awaits it will block for
     * exactly as long as the fetch takes.
     */
    override async userProfileDidChange() {

        // Replace with a new pending promise for this cycle. Any UI that
        // awaits userDataReady will block until we call the resolver below.
        this.userDataReady = new Promise<boolean>(resolve => {
            this._userDataReadyResolve = resolve
        })

        if (IS(this.userProfile)) {
            // Read unseen counts embedded by the server on the userProfile object
            // (only present for single-company users — the server cannot guess a
            // multi-company user's chosen active company at login), then strip
            // the field immediately so it does not leak elsewhere.
            const embedded = this.userProfile.unseenCounts
            if (embedded) {
                this.notificationStore.updateGlobalCounts(embedded)
                delete this.userProfile.unseenCounts
            }

            // Seed the personal favorites set from the welcome-message profile.
            this._favoriteProcurementIds = new Set(
                (this.userProfile.favoriteProcurementIds ?? []).map(favoriteId => favoriteId + "")
            )

            try {
                await this.companyAffiliationStore.retrieveMyCompanyAffiliations(YES)
                await this.currentCompanyAffiliation
            }
            catch (error) {
                // Authentication remains valid even when company-affiliation RPCs
                // are temporarily unavailable. The landing view reads the
                // store's failed state and offers a retry instead of claiming
                // that the user has no company.
                console.error("RHCore: company affiliation hydration failed during login", error)
            }
            // Multi-company users (no embedded counts above) fetch here, now
            // that the active company is known.
            if (!embedded) {
                this.notificationStore.refreshGlobalCounts()
            }
        }
        else {
            this._currentCompanyAffiliation = undefined
            this._currentCompanyAffiliationPromise = undefined
            this.companyAffiliationStore.clear()
            // Do not retain company- and user-scoped private messages across logout.
            this.privateMessageStore.clear()
            // Reset global counts to zero on logout so stale badges
            // are not shown to a subsequently logged-in user.
            this.notificationStore.clearAllCounts()
            // Clear per-procurement offer-message counts so they are not
            // carried over to the next session.
            this.notificationStore.clearOfferMessageCounts()
            // Drop favorites so one user's stars never show for the next session.
            this._favoriteProcurementIds = new Set()
            // Reset every remaining cached store so a subsequently logged-in
            // user on the same device never sees a stale user's data.
            this.procurementStore.clear()
            this.offerStore.clear()
            this.contractStore.clear()
            this.qaStore.clear()
            this.offererConfigurationStore.clear()
        }

        super.userProfileDidChange()

        // All data is ready — unblock any awaiting UI.
        this._userDataReadyResolve?.(IS(this.userProfile))
        this._userDataReadyResolve = undefined

        // Resolve the one-shot sessionInitialised promise on the first cycle.
        // Subsequent cycles leave it resolved (calling a resolved promise's
        // resolver is a no-op).
        this._resolveSessionInitialised()

    }


    /**
     * Logs the user out and atomically replaces the current authentication route.
     * The target route is installed before `userDidLogOut` is broadcast so root
     * listeners can never restart work for the stale authenticated route.
     */
    async logOutAndApplyRoute(targetRoute: UIRoute) {
        const response = await fetch("/auth/logout", { method: "GET", credentials: "include" })
        if (!response.ok) {
            throw new Error("Logout failed")
        }

        const wasUserLoggedIn = this.isUserLoggedIn
        this._isUserLoggedIn = NO
        this._userProfile = undefined

        // Run the normal profile cleanup without allowing CBCore to wipe the
        // route. The login flag is cleared first, so super.userProfileDidChange()
        // does not invoke the default userDidLogOut route transition.
        await this.userProfileDidChange()

        // A server-side logout only invalidates the database session. Replace
        // the client as well so the old authenticated socket cannot continue to
        // send requests with its handshake-time session snapshot.
        this.reloadSocketConnection()

        // This credential-boundary transition has already been confirmed by the
        // caller. Replace history so one-time invitation/reset tokens cannot be
        // reached with Back, and do it before listeners inspect currentRoute.
        targetRoute.applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()

        if (wasUserLoggedIn) {
            this.broadcastMessageInRootViewTree({
                name: CBCore.broadcastEventName.userDidLogOut,
                parameters: nil
            })
        }
        this.updateLinkTargets()
    }


    override get userProfile(): RHUserProfile | undefined {
        return super.userProfile as RHUserProfile | undefined
    }

    override set userProfile(userProfile: RHUserProfile | undefined) {
        super.userProfile = userProfile
    }


    // ── Favorites ("lemmikud") ────────────────────────────────────────────────
    //
    // Personal per-user procurement bookmarks. The set is seeded from the welcome
    // message and is the single client-side source of truth: views read it to draw
    // the star + the "Lemmikud" filter, and flip it via toggleProcurementFavorite.

    isProcurementFavorite(procurementId: string): boolean {
        return this._favoriteProcurementIds.has(procurementId + "")
    }


    favoriteProcurementIds(): string[] {
        return [...this._favoriteProcurementIds]
    }

    async toggleProcurementFavorite(procurementId: string) {
        const id = procurementId + ""
        const shouldBeFavorite = NO == this._favoriteProcurementIds.has(id)

        // Optimistic: flip locally + repaint immediately, then persist.
        if (shouldBeFavorite) {
            this._favoriteProcurementIds.add(id)
        }
        else {
            this._favoriteProcurementIds.delete(id)
        }
        this._broadcastFavoritesDidChange()

        try {
            const response = await SocketClient.SetProcurementFavorite({
                procurementId: id,
                isFavorite: shouldBeFavorite
            })
            if (!response.result?.success) {
                throw new Error("SetProcurementFavorite: server rejected the request")
            }
            this.procurementStore.invalidatePublishedProcurements([id])
        }
        catch (error) {
            // Persist failed — revert the optimistic change and repaint.
            if (shouldBeFavorite) {
                this._favoriteProcurementIds.delete(id)
            }
            else {
                this._favoriteProcurementIds.add(id)
            }
            this._broadcastFavoritesDidChange()
            console.error("Failed to toggle procurement favorite", error)
        }
    }

    private _broadcastFavoritesDidChange() {
        this.broadcastMessageInRootViewTree({
            name: RHCore.broadcastEventName.favoritesDidChange,
            parameters: nil
        })
    }


}
